import type { BlockStateUnionType } from '@unifyapps/defs/types/block';
import type { RefObject } from 'react';
import type { InterfaceEntity } from '@unifyapps/defs/types/interface';
import { invariant } from 'ts-invariant';
import type { PageVariableState } from '@unifyapps/defs/types/pageVariable';
import type { BlockControlMethod } from '../../components/BlockDefinition';
import type {
  DataSourceMetaType,
  LocationInStore,
  QueryRequestResult,
} from '../../stores/GlobalStateStore';
import { getCombinedBlockMethodsInBlocksState } from './utils/getCombinedBlockMethodsInBlocksState';
import { getCombinedBlockState } from './utils/getCombinedBlockState';
import { getCombinedDataSourceState } from './utils/getCombinedDataSourceState';
import { getCombinedPageVariables } from './utils/getCombinedPageVariables';
import { ComputeContextKeys, contextUtils } from './const';
import getInterfaceRecordForState from './utils/getInterfaceRecordForState';
import { adaptPageVariablesForContext } from './utils/adaptPageVariablesForContext';

export type ComputeContextType = {
  runtimeBlocks?: Record<string, BlockStateUnionType | undefined>;
  [key: string]: unknown;
};

class BuildComputeContext {
  private blockMethods?: Record<string, BlockControlMethod[] | undefined>;
  private blockStates?: Record<string, BlockStateUnionType | undefined>;
  private blockRefs?: Record<string, RefObject<unknown> | undefined>;
  private dataSources?: Record<string, QueryRequestResult | undefined>;
  private dataSourceMethods?: Record<string, unknown>;
  private dataSourcesMeta?: Record<string, DataSourceMetaType | undefined>;
  private pageInputs?: Record<string, unknown>;
  private location?: Record<string, unknown>;
  private userContext?: Record<string, unknown>;
  private pageVariables?: Record<string, PageVariableState>;
  private pageVariableMethods?: Record<string, unknown>;
  private pageFunctions?: Record<string, unknown>;
  private interfaceSessionStorage?: Record<string, unknown>;
  private interfaceRecord?: InterfaceEntity;
  private blocksRuntimeState?: Record<string, Record<string, unknown>>;
  private walkthrough?: Record<string, unknown>;
  private envVariables?: Record<string, string>;

  setMethods(methods: {
    blockMethods?: Record<string, BlockControlMethod[] | undefined>;
    dataSourceMethods?: Record<string, unknown>;
    pageVariableMethods?: Record<string, unknown>;
  }) {
    this.blockMethods = methods.blockMethods;
    this.dataSourceMethods = methods.dataSourceMethods;
    this.pageVariableMethods = methods.pageVariableMethods;
    return this;
  }

  setBlockStates(blockStates: Record<string, BlockStateUnionType | undefined>) {
    this.blockStates = blockStates;
    return this;
  }

  setBlockRefs(refs: Record<string, RefObject<unknown> | undefined>) {
    this.blockRefs = refs;
    return this;
  }

  setWalkthrough(walkthrough: Record<string, unknown>) {
    this.walkthrough = walkthrough;
    return this;
  }

  setDataSources(dataSources: Record<string, QueryRequestResult | undefined>) {
    this.dataSources = dataSources;
    return this;
  }

  setDataSourcesMeta(meta: Record<string, DataSourceMetaType | undefined>) {
    this.dataSourcesMeta = meta;
    return this;
  }

  setPageInputs(inputs: Record<string, unknown>) {
    this.pageInputs = inputs;
    return this;
  }

  setLocation(location: LocationInStore) {
    this.location = location;
    return this;
  }

  setUserContext(context: Record<string, unknown>) {
    this.userContext = context;
    return this;
  }

  setPageVariables(variables: Record<string, PageVariableState>) {
    this.pageVariables = variables;
    return this;
  }

  setPageFunctions(functions: Record<string, unknown>) {
    this.pageFunctions = functions;
    return this;
  }

  setInterfaceSessionStorage(storage: Record<string, unknown>) {
    this.interfaceSessionStorage = storage;
    return this;
  }

  setInterfaceRecord(record: InterfaceEntity) {
    this.interfaceRecord = record;
    return this;
  }

  setBlocksRuntimeState(state?: Record<string, Record<string, unknown>>) {
    this.blocksRuntimeState = state;
    return this;
  }

  setEnvVariables(envVariables: Record<string, string>) {
    this.envVariables = envVariables;
    return this;
  }

  build() {
    invariant(this.blockStates !== undefined, 'blockStates must be set before building');
    invariant(this.blockRefs !== undefined, 'blockRefs must be set before building');
    invariant(this.dataSources !== undefined, 'dataSources must be set before building');
    invariant(this.dataSourcesMeta !== undefined, 'dataSourcesMeta must be set before building');
    invariant(this.pageInputs !== undefined, 'pageInputs must be set before building');
    invariant(this.location !== undefined, 'location must be set before building');
    invariant(this.userContext !== undefined, 'userContext must be set before building');
    invariant(this.pageVariables !== undefined, 'pageVariables must be set before building');
    invariant(this.pageFunctions !== undefined, 'pageFunctions must be set before building');
    invariant(
      this.interfaceSessionStorage !== undefined,
      'interfaceSessionStorage must be set before building',
    );
    invariant(this.interfaceRecord !== undefined, 'interfaceRecord must be set before building');
    invariant(this.blockMethods !== undefined, 'blockMethods must be set before building');
    invariant(
      this.dataSourceMethods !== undefined,
      'dataSourceMethods must be set before building',
    );
    invariant(
      this.pageVariableMethods !== undefined,
      'pageVariableMethods must be set before building',
    );
    // invariant(this.walkthrough !== undefined, 'walkthrough must be set before building');
    invariant(this.envVariables !== undefined, 'envVariables must be set before building');

    const blocksStateWithMethod = getCombinedBlockMethodsInBlocksState({
      blocksState: this.blockStates,
      blocksMethods: this.blockMethods,
    });

    const combinedBlockState = getCombinedBlockState({
      blocksState: blocksStateWithMethod,
      blocksRuntimeState: this.blocksRuntimeState,
    });

    const combinedDataSourceState = getCombinedDataSourceState({
      dataSourcesState: this.dataSources,
      dataSourceMethods: this.dataSourceMethods,
    });

    const adaptedPageVariables = adaptPageVariablesForContext(this.pageVariables);

    const combinedPageVariables = getCombinedPageVariables({
      pageVariables: adaptedPageVariables,
      pageVariablesMethods: this.pageVariableMethods,
    });

    return {
      blockRefs: this.blockRefs,
      dataSourcesMeta: this.dataSourcesMeta,
      ...combinedBlockState,
      ...combinedDataSourceState,
      [ComputeContextKeys.WALKTHROUGH]: this.walkthrough,
      [ComputeContextKeys.RUNTIME_BLOCKS]: combinedBlockState.runtimeBlocks,
      [ComputeContextKeys.INTERFACE_RECORD]: getInterfaceRecordForState(this.interfaceRecord),
      [ComputeContextKeys.PAGE_INPUTS]: this.pageInputs,
      [ComputeContextKeys.LOCATION]: this.location,
      [ComputeContextKeys.USER_CONTEXT]: this.userContext,
      /**
       * Page Variables and Functions Assignment
       *
       * @deprecated This implementation is slated for removal in a future update.
       *
       * Historical Context:
       * - Previously, page variables and functions were stored under objects
       *   with keys __PAGE_VARS__ and __PAGE_FUNCTIONS__ respectively.
       * - This was done to maintain backward compatibility with existing code.
       *
       * Current Implementation:
       * - Page variables and functions are now stored flat in the compute context.
       *
       *
       */
      ...this.pageFunctions,
      [ComputeContextKeys.PAGE_FUNCTIONS]: this.pageFunctions,
      ...combinedPageVariables,
      [ComputeContextKeys.PAGE_VARIABLES]: adaptedPageVariables,

      [ComputeContextKeys.SESSION_STORAGE]: {
        ...this.interfaceSessionStorage,
        // spreading data here for backward compact, as previously data was exposed directly at the first level
        ...(this.interfaceSessionStorage.data ?? {}),
      },
      [ComputeContextKeys.UTILS]: contextUtils,
      [ComputeContextKeys.ENV_VARIABLES]: this.envVariables,
    };
  }
}

export default BuildComputeContext;
